/**
 * Office Page
 *
 * Kontoroversikt view with room list, interactive floorplan and room info modal.
 */

import React from "react";
import { PanelSplitView } from "@/components/layout/PanelSplitView";
import { Floorplan } from "@/components/office/Floorplan/Floorplan";
import { MeetingRoomModal } from "@/components/office/MeetingRoomModal/MeetingRoomModal";
import { ActionButton } from "@/components/ui/buttons/ActionButton";
import { PointingHint } from "@/components/ui/feedback/PointingHint";
import { SectionHeader } from "@/components/ui/navigation/SectionHeader";
import { RotateCw } from "lucide-react";
import { OFFICE_ROOMS } from "@/features/office/rooms";
import type { OfficeRoom, RoomInfoEntry } from "@/features/office/types";
import styles from "./OfficePage.module.css";

export const OfficePage: React.FC = () => {
  const [hoveredRoom, setHoveredRoom] = React.useState<string | null>(null);
  const [detailRoomId, setDetailRoomId] = React.useState<string | null>(null);
  const [modalRoomId, setModalRoomId] = React.useState<string | null>(null);
  const [rotation, setRotation] = React.useState<number>(0);
  const [isPanelCollapsed, setIsPanelCollapsed] = React.useState(false);

  const detailRoom = React.useMemo<OfficeRoom | null>(() => {
    if (!detailRoomId) {
      return null;
    }

    return OFFICE_ROOMS.find((room) => room.id === detailRoomId) ?? null;
  }, [detailRoomId]);

  const modalRoom = React.useMemo<OfficeRoom | null>(() => {
    if (!modalRoomId) {
      return null;
    }

    return OFFICE_ROOMS.find((room) => room.id === modalRoomId) ?? null;
  }, [modalRoomId]);

  const handleRoomHover = React.useCallback((roomId: string | null) => {
    setHoveredRoom(roomId);
  }, []);

  const handleDetailSelect = React.useCallback((roomId: string) => {
    setDetailRoomId(roomId);
    setHoveredRoom(roomId);
    setIsPanelCollapsed(false);
  }, []);

  const handleModalSelect = React.useCallback((roomId: string) => {
    setModalRoomId(roomId);
  }, []);

  const handleModalClose = React.useCallback(() => {
    setModalRoomId(null);
  }, []);

  const handleBackgroundClick = React.useCallback(() => {
    setDetailRoomId(null);
  }, []);

  const handleRotate = React.useCallback((amount: number) => {
    setRotation((prev) => (prev + amount + 360) % 360);
  }, []);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setDetailRoomId(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div className={styles.page} data-testid="office-page">
      <PanelSplitView
        variant="dark"
        collapsed={isPanelCollapsed}
        onCollapse={setIsPanelCollapsed}
        panel={
          <div className={styles.panel}>
            {!detailRoom ? (
              <>
                <SectionHeader as="h3" size="md" className={styles.panelTitle}>
                  Kontoroversikt
                </SectionHeader>
                <p className={styles.panelDescription}>
                  Ha musa over rommene for å sjekke navnet, og klikk for å se
                  mer informasjon.
                </p>
                <ul className={styles.roomList}>
                  {OFFICE_ROOMS.map((room) => {
                    const isActive = hoveredRoom === room.id;
                    return (
                      <li key={room.id}>
                        <button
                          type="button"
                          className={`${styles.roomButton} ${
                            isActive ? styles.roomButtonActive : ""
                          }`}
                          onMouseEnter={() => handleRoomHover(room.id)}
                          onMouseLeave={() => handleRoomHover(null)}
                          onFocus={() => handleRoomHover(room.id)}
                          onBlur={() => handleRoomHover(null)}
                          onClick={() => handleModalSelect(room.id)}
                          data-testid={`room-${room.id}`}
                        >
                          {room.name}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : (
              <div>
                <div className={styles.detailsHeader}>
                  <button
                    type="button"
                    className={styles.backButton}
                    onClick={() => setDetailRoomId(null)}
                  >
                    ← Tilbake til romliste
                  </button>
                </div>
                <section>
                  <h3 className={styles.detailsTitle}>{detailRoom.name}</h3>
                  <table className={styles.detailsTable}>
                    <tbody>
                      {(
                        [
                          detailRoom.info.capacity,
                          detailRoom.info.functionality,
                          detailRoom.info.oldName,
                        ].filter(Boolean) as RoomInfoEntry[]
                      ).map((entry) => (
                        <tr key={`${detailRoom.id}-${entry.friendlyName}`}>
                          <td>{entry.friendlyName}</td>
                          <td>{entry.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              </div>
            )}
          </div>
        }
      >
        <div
          className={styles.mapContainer}
          onClick={handleBackgroundClick}
          role="presentation"
        >
          <div className={styles.floorplanControls}>
            <PointingHint.Anchored
              direction="top-left"
              storageKey="floorplan-rotation"
              variant="outlined"
              offset={0}
              target={
                <ActionButton
                  label="Rotér"
                  variant="secondary"
                  size="small"
                  rounded
                  icon={<RotateCw size={16} aria-hidden={true} />}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleRotate(90);
                  }}
                />
              }
            >
              Bruk rotér-knappen for å endre retningen på etasjeplanen.
            </PointingHint.Anchored>
          </div>
          <Floorplan
            className={styles.floorplan}
            hoveredRoom={hoveredRoom}
            selectedRoom={detailRoom?.id ?? null}
            onRoomHover={handleRoomHover}
            onRoomSelect={handleDetailSelect}
            style={{ transform: `rotate(${rotation}deg)` }}
          />
        </div>
      </PanelSplitView>
      <MeetingRoomModal
        isOpen={!!modalRoom}
        room={modalRoom}
        onClose={handleModalClose}
      />
    </div>
  );
};

export default OfficePage;
